import { Button, Grid, Stack, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import Add from '@mui/icons-material/Add';
import OpenInNew from '@mui/icons-material/OpenInNew';
import Sort from '@mui/icons-material/Sort';
import {
  HeaderRow,
  HeaderTextWrapper,
  HeaderTitle,
  LoadMoreButton,
  LoadMoreWrapper,
  PageContainer,
  WelcomeName,
} from '~/modules/matters/MattersListLayout.styles';
import * as Sentry from '@sentry/react';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMattersQuery } from '~/modules/matters/useMattersQuery';
import { useMatterMutations } from '~/modules/matters/useMatterMutations';
import { MatterStatus, RetainerStatus } from '~/gql/graphql';
import type { CreateMatterInput } from '~/gql/graphql';
import type { User } from '~/shared/types/connect';
import type { MattersTabValue } from '~/shared/types';
import type { MatterViewModel } from '~/modules/matters/matters.types';
import { useToast } from '~/shared/contexts/ToastContext';
import { MatterCard } from '~/modules/matters/MatterCard';
import { getMatterCardDerivedProps } from '~/modules/matters/matterCardUtils';
import { MattersEmptyView } from '~/modules/matters/MattersEmptyView';
import { MattersListSkeleton } from '~/modules/matters/MattersListSkeleton';
import { MattersListErrorState } from '~/modules/matters/MattersListErrorState';
import {
  CreateMatterModal,
  type CreateMatterFormData,
} from '~/modules/matters/modals/CreateMatterModal';
import CloseMatterModal from '~/modules/matters/modals/CloseMatterModal';
import { MattersTabs } from '~/modules/matters/MattersTabs';
import AttorneyMattersFilter from '~/modules/matters/AttorneyMattersFilter';
import { ExpertFilterChip } from '~/modules/matters/ExpertFilterChip';
import DropdownFilter from '~/modules/matters/DropdownFilter';
import {
  useMattersList,
  SORT_OPTION_LABELS,
  SORT_OPTIONS,
  SortOption,
  FilterByOption,
} from '~/modules/matters';
import { buildGetExpertByIdFromMatters } from '~/modules/matters/expertResolverFromMatters';
import { RAILS_EXPERTS_URL } from '~/shared/config';
import { useMyMatterExpertsQuery } from '~/modules/matters/useMyMatterExpertsQuery';

const EMPTY_TITLES: Record<MattersTabValue, string> = {
  active: 'No active matters.',
  inactive: 'No inactive matters.',
  closed: 'No closed matters.',
};

interface Props {
  currentUser: User;
}

const AttorneyMattersView = ({ currentUser }: Props) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const expertFromUrl = searchParams.get('expert') ?? null;
  const [expertFilterId, setExpertFilterIdState] = useState<string | null>(
    expertFromUrl,
  );
  const prevExpertFromUrl = useRef(expertFromUrl);
  if (prevExpertFromUrl.current !== expertFromUrl) {
    prevExpertFromUrl.current = expertFromUrl;
    setExpertFilterIdState(expertFromUrl);
  }
  const setExpertFilterId = (id: string | null) => {
    setExpertFilterIdState(id);
    if (id === null) {
      searchParams.delete('expert');
    } else {
      searchParams.set('expert', id);
    }
    setSearchParams(searchParams, { replace: true });
  };

  const { showToast } = useToast();
  const { matters, isLoading, error } = useMattersQuery();
  const {
    createMatter: createMatterMutation,
    updateMatter: updateMatterMutation,
    closeMatter: closeMatterMutation,
    isCreating: isCreatingMatter,
    isClosing: isClosingMatter,
  } = useMatterMutations();
  const { experts: pickerExperts } = useMyMatterExpertsQuery();
  const getExpertById = useMemo(
    () => buildGetExpertByIdFromMatters(matters, pickerExperts),
    [matters, pickerExperts],
  );

  const getRetainersForMatter = useCallback(
    (targetMatterId: string) => {
      const found = matters.find(
        (matterItem) => matterItem.id === targetMatterId,
      );
      if (!found) {
        return [];
      }
      return found.matterExperts.flatMap((row) => row.retainers);
    },
    [matters],
  );

  const [mattersSubTab, setMattersSubTab] = useState<MattersTabValue>('active');
  const [sortBy, setSortBy] = useState<SortOption>(SortOption.UpdatedAtDesc);
  const [filterBy, setFilterBy] = useState<FilterByOption>(FilterByOption.All);
  const [createMatterModalOpen, setCreateMatterModalOpen] = useState(false);
  const [matterToClose, setMatterToClose] = useState<MatterViewModel | null>(
    null,
  );

  const {
    myMatters,
    filteredMatters,
    visibleMatters,
    hasMoreMatters,
    loadMore,
    activeCount,
    inactiveCount,
    closedCount,
    expertOptions,
  } = useMattersList({
    matters,
    currentUser,
    mattersSubTab,
    sortBy,
    filterBy,
    expertFilterId,
    getRetainersForMatter,
    getExpertById,
  });

  function getTotalBilledForMatter(matterId: string): number {
    return (
      getRetainersForMatter(matterId)
        .filter((retainer) => retainer.status === RetainerStatus.Approved)
        .reduce((sum, retainer) => sum + retainer.total, 0) / 100
    );
  }

  const handleCreateMatter = async (data: CreateMatterFormData) => {
    const input: CreateMatterInput = {
      title: data.title,
      caseDescription: data.caseDescription,
      caseNumber: data.caseNumber || undefined,
      courtName: data.courtName || undefined,
      location: data.location || undefined,
      keyDates: data.keyDates || undefined,
      nextDeadline: data.nextDeadline || undefined,
      travelPlans: data.travelPlans || undefined,
      writtenReportNeeded: data.writtenReportNeeded || undefined,
      expertId: data.expertId || undefined,
    };
    try {
      const matter = await createMatterMutation(input);
      setCreateMatterModalOpen(false);
      if (matter?.id) {
        navigate(`/matters/${matter.id}`, { state: { openExpertsTab: true } });
      }
    } catch (error) {
      Sentry.captureException(error, {
        tags: { feature: 'create_matter_attorney_list' },
      });
      const detail =
        error instanceof Error ? error.message : 'Please try again.';
      showToast(`Could not create matter. ${detail}`, 'error');
      throw error;
    }
  };

  const handleReopenMatter = async (matter: MatterViewModel) => {
    try {
      await updateMatterMutation(matter.id, { status: MatterStatus.Active });
    } catch (error) {
      Sentry.captureException(error, {
        tags: { feature: 'matter_reopen_attorney_list' },
        extra: { matterId: matter.id },
      });
      const message =
        error instanceof Error
          ? error.message
          : 'Something went wrong. Please try again.';
      showToast(message, 'error');
    }
  };

  const handleStatusChange = async (
    matter: MatterViewModel,
    value: MatterStatus,
  ) => {
    if (value === MatterStatus.Closed) {
      setMatterToClose(matter);
      return;
    }
    try {
      await updateMatterMutation(matter.id, { status: value });
    } catch (error) {
      Sentry.captureException(error, {
        tags: { feature: 'matter_status_attorney_list' },
        extra: { matterId: matter.id, status: value },
      });
      const message =
        error instanceof Error
          ? error.message
          : 'Something went wrong. Please try again.';
      showToast(message, 'error');
    }
  };

  const handleConfirmCloseMatter = async () => {
    if (!matterToClose) {
      return;
    }
    try {
      await closeMatterMutation(matterToClose.id);
      setMatterToClose(null);
    } catch (error) {
      Sentry.captureException(error, {
        tags: { feature: 'matter_close_attorney_list' },
        extra: { matterId: matterToClose.id },
      });
      const message =
        error instanceof Error
          ? error.message
          : 'Could not close matter. Please try again.';
      showToast(message, 'error');
    }
  };

  const attorneyFilterExpertOptions = expertOptions.map(({ id, expert }) => ({
    id,
    name: expert.name ?? '',
  }));

  const selectedFilterExpert =
    expertFilterId !== null ? getExpertById(expertFilterId) : undefined;

  const showSkeleton = isLoading;
  const showError = !isLoading && Boolean(error);
  const showInitialEmpty = !isLoading && !error && myMatters.length === 0;
  const showContent = !showSkeleton && !showError && !showInitialEmpty;

  return (
    <PageContainer>
      <HeaderRow>
        <HeaderTextWrapper>
          <HeaderTitle variant="h5" fontWeight={700}>
            Welcome back{currentUser.name ? ',' : ''}
            {currentUser.name ? (
              <>
                {' '}
                <WelcomeName>{currentUser.name}</WelcomeName>
              </>
            ) : null}
          </HeaderTitle>
          <HeaderSubtitle variant="body2" color="text.secondary">
            Your latest matters and pending retainer approvals.
          </HeaderSubtitle>
        </HeaderTextWrapper>
        <ControlsRow>
          <CreateMatterButton
            variant="contained"
            size="small"
            startIcon={<CreateMatterButtonIcon />}
            onClick={() => setCreateMatterModalOpen(true)}
          >
            Create Matter
          </CreateMatterButton>
        </ControlsRow>
      </HeaderRow>

      {showSkeleton && <MattersListSkeleton />}
      {showError && (
        <MattersListErrorState
          message={error?.message || 'Failed to load matters'}
        />
      )}
      {showInitialEmpty && (
        <MattersEmptyView
          title="Get started with your first matter"
          description="Find an expert in the directory, or create a matter and add experts to it."
        >
          <EmptyStateButtonRow>
            <FindExpertsEmptyButton
              component="a"
              href={RAILS_EXPERTS_URL}
              target="_blank"
              rel="noopener noreferrer"
              variant="contained"
              size="medium"
              endIcon={<FindExpertsIcon />}
              aria-label="Find experts (opens in new tab)"
            >
              Find experts
            </FindExpertsEmptyButton>
            <CreateMatterEmptyButton
              variant="outlined"
              size="medium"
              startIcon={<CreateMatterButtonIcon />}
              onClick={() => setCreateMatterModalOpen(true)}
            >
              Create a matter
            </CreateMatterEmptyButton>
          </EmptyStateButtonRow>
        </MattersEmptyView>
      )}
      {showContent && (
        <>
          <MattersTabs
            value={mattersSubTab}
            onChange={(_, value) => setMattersSubTab(value)}
            activeCount={activeCount}
            inactiveCount={inactiveCount}
            closedCount={closedCount}
            toolbar={
              <>
                {selectedFilterExpert && (
                  <ExpertFilterChip
                    expert={selectedFilterExpert}
                    onClear={() => setExpertFilterId(null)}
                  />
                )}
                <AttorneyMattersFilter
                  expertFilterId={expertFilterId}
                  filterBy={filterBy}
                  expertOptions={attorneyFilterExpertOptions}
                  onExpertChange={setExpertFilterId}
                  onFilterByChange={setFilterBy}
                />
                <DropdownFilter
                  ariaLabel="Sort by"
                  labelPrefix="Sort:"
                  valueLabel={SORT_OPTION_LABELS[sortBy]}
                  icon={<Sort fontSize="small" color="action" aria-hidden />}
                  options={SORT_OPTIONS}
                  onSelect={(value: SortOption) => setSortBy(value)}
                />
              </>
            }
          />

          {filteredMatters.length === 0 ? (
            <MattersEmptyView
              title={EMPTY_TITLES[mattersSubTab]}
              description="Try adjusting your filters or sort order."
              compact
            />
          ) : (
            <>
              <Grid container spacing={2}>
                {visibleMatters.map((matter) => {
                  const derived = getMatterCardDerivedProps(matter, {
                    retainers: getRetainersForMatter(matter.id),
                    getExpertById,
                  });
                  return (
                    <Grid size={12} key={matter.id}>
                      <MatterCard
                        matter={matter}
                        otherParty={derived.otherParty}
                        experts={derived.experts}
                        expertRetainerStatuses={derived.expertRetainerStatuses}
                        totalBilled={getTotalBilledForMatter(matter.id)}
                        hasPendingRetainer={derived.hasPendingRetainer}
                        isPendingRetainerInitiatedByExpert={
                          derived.isPendingRetainerInitiatedByExpert
                        }
                        hasFundedRetainer={derived.hasFundedRetainer}
                        isAttorneyView
                        matterTab={mattersSubTab}
                        activeRetainerAmount={derived.activeRetainerAmount}
                        onReopenMatter={handleReopenMatter}
                        onStatusChange={handleStatusChange}
                        onAcceptRetainerRequest={(matterParam) => {
                          const pendingRetainer = getRetainersForMatter(
                            matterParam.id,
                          ).find(
                            (retainer) =>
                              retainer.status === RetainerStatus.Pending,
                          );
                          if (pendingRetainer) {
                            navigate(`/matters/${matterParam.id}`, {
                              state: {
                                openAgreementRequestId: pendingRetainer.id,
                              },
                            });
                          }
                        }}
                      />
                    </Grid>
                  );
                })}
              </Grid>
              {hasMoreMatters && (
                <LoadMoreWrapper>
                  <LoadMoreButton
                    variant="outlined"
                    size="medium"
                    onClick={loadMore}
                  >
                    Load more
                  </LoadMoreButton>
                </LoadMoreWrapper>
              )}
            </>
          )}
        </>
      )}

      <CreateMatterModal
        isOpen={createMatterModalOpen}
        onClose={() => setCreateMatterModalOpen(false)}
        experts={pickerExperts}
        onSubmit={handleCreateMatter}
        isSubmitting={isCreatingMatter}
      />
      <CloseMatterModal
        isOpen={Boolean(matterToClose)}
        onClose={() => setMatterToClose(null)}
        matterTitle={matterToClose?.title}
        onConfirm={handleConfirmCloseMatter}
        isClosing={isClosingMatter}
      />
    </PageContainer>
  );
};

const HeaderSubtitle = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.body2.fontSize,
}));

const ControlsRow = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  flexShrink: 0,
  width: '100%',
  [theme.breakpoints.up('sm')]: {
    width: 'auto',
  },
}));

const CreateMatterButton = styled(Button)(({ theme }) => ({
  fontSize: theme.typography.button.fontSize,
  fontWeight: 600,
  minHeight: 44,
  padding: theme.spacing(1.25, 2.5),
  whiteSpace: 'nowrap',
  backgroundColor: theme.palette.buttonCreateMatter,
  color: theme.palette.darkNavy,
  '&:hover': {
    backgroundColor: theme.palette.teal.dark,
  },
  width: '100%',
  [theme.breakpoints.up('sm')]: {
    width: 'auto',
  },
})) as typeof Button;

const CreateMatterButtonIcon = styled(Add)(({ theme }) => ({
  fontSize: theme.typography.body2.fontSize,
}));

const FindExpertsIcon = styled(OpenInNew)(({ theme }) => ({
  fontSize: theme.typography.body2.fontSize,
}));

const EmptyStateButtonRow = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'stretch',
  justifyContent: 'center',
  gap: theme.spacing(2),
  width: '100%',
  maxWidth: 480,
  margin: '0 auto',
}));

const FindExpertsEmptyButton = styled(Button)(({ theme }) => ({
  flex: '1 1 0',
  minWidth: 0,
  fontSize: theme.typography.button.fontSize,
  minHeight: 44,
  padding: theme.spacing(1.25, 2),
  backgroundColor: theme.palette.darkNavy,
  color: theme.palette.common.white,
  '&:hover': {
    backgroundColor: theme.palette.amount?.dark ?? theme.palette.primary.dark,
  },
})) as typeof Button;

const CreateMatterEmptyButton = styled(Button)(({ theme }) => ({
  flex: '1 1 0',
  minWidth: 0,
  fontSize: theme.typography.button.fontSize,
  minHeight: 44,
  padding: theme.spacing(1.25, 2),
  borderColor: theme.palette.darkNavy,
  color: theme.palette.text.primary,
  '&:hover': {
    borderColor: theme.palette.grey[400],
    backgroundColor: theme.palette.action.hover,
  },
})) as typeof Button;

export default AttorneyMattersView;
